/**
 * InstallBanner.tsx
 * PWA 설치 안내 배너
 * - Chrome/Edge: beforeinstallprompt로 원클릭 설치
 * - Safari: 공유 메뉴 안내 (자동 설치 불가)
 * - 이미 PWA로 실행 중이면 표시 안 함
 * - X 버튼은 현재 보기에서만 닫음 (영구 dismiss 없음 — 새로고침 시 재표시)
 */

import { useState, useEffect } from "react";
import { X, Share, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

// 과거 영구 dismiss 키 (이젠 사용 안 함, 마운트 시 정리)
const LEGACY_DISMISS_KEY = "pwa_install_dismissed";

// "설치했음" 체크 → 쿠키 저장 (iOS 등 자동 감지 불가 환경에서 영구 미표시)
const INSTALLED_COOKIE = "pwa_installed";
const setCookie = (name: string, val: string, days: number) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 864e5);
  document.cookie = `${name}=${val}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
};
const getCookie = (name: string) =>
  document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1];

export default function InstallBanner() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // 과거 영구 dismiss 기록 정리 — PWA 미설치 시 항상 표시 정책으로 변경됨
    localStorage.removeItem(LEGACY_DISMISS_KEY);

    // 이미 PWA로 실행 중이면 표시 안 함
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || (window.navigator as any).standalone === true;
    if (isStandalone) return;

    // 사용자가 "설치했음" 누른 적 있으면 (쿠키) 표시 안 함
    if (getCookie(INSTALLED_COOKIE) === "1") return;

    // Safari 감지
    const ua = navigator.userAgent;
    const safari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua);
    setIsSafari(safari);

    // Chrome/Edge: beforeinstallprompt 이벤트 캡처
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Safari: 즉시 안내 표시 (beforeinstallprompt 미지원이므로 대기 불필요)
    if (safari) {
      setShow(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShow(false);
      }
      setDeferredPrompt(null);
    }
  };

  // X 버튼: 현재 보기에서만 닫음 (영구 저장 안 함). 새로고침 시 다시 표시됨.
  const handleDismiss = () => {
    setShow(false);
  };

  // "설치했음" 체크 → 쿠키 저장 → 다음부터 안 띄움 (1년)
  const handleInstalledCheck = () => {
    setCookie(INSTALLED_COOKIE, "1", 365);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 safe-area-bottom">
      {/* 2줄 구조 — 1줄: 아이콘+텍스트+(안드로이드 설치)+설치했음+X / 2줄: Safari 공유→추가 단계.
          좁은 폰에서 텍스트가 글자단위로 쪼개지던 문제 해결 (#373) */}
      <div className="mx-auto max-w-lg flex flex-col gap-2">
        {/* 1행 — 가로 중앙정렬 (X 는 우상단 절대배치) */}
        <div className="relative flex items-center justify-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700 to-blue-900 text-xs font-black text-white">
            TP
          </div>
          <div className="min-w-0 text-center">
            <div className="text-sm font-bold text-white">{t("install.title")}</div>
            <div className="text-[11px] text-slate-400 break-keep">{t("install.desc")}</div>
          </div>
          {!isSafari && deferredPrompt && (
            <button
              onClick={handleInstall}
              className="shrink-0 rounded-lg bg-gradient-to-r from-amber-500 to-orange-400 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-amber-500/25"
            >
              {t("install.btn")}
            </button>
          )}
          {/* 설치했음 — 비-Safari(안드로이드)는 1행, 블루 버튼 */}
          {!isSafari && (
            <button
              onClick={handleInstalledCheck}
              className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-[11px] font-bold text-white hover:bg-blue-500 transition-colors"
            >
              {t("install.installed") || "설치했음"}
            </button>
          )}
          <button onClick={handleDismiss} className="absolute right-0 top-0 text-slate-500 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* 2행 (Safari) — 안내 문구 + 공유→추가 아이콘 + 설치했음. 아이콘만으론 위치를 모르는 분이 많아 문구 병기 */}
        {isSafari && (
          <div className="flex flex-col items-center gap-1.5">
            <p className="text-[11px] leading-snug text-slate-300 text-center break-keep">
              {t("install.iosGuide")}
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-1">
              <div className="flex flex-col items-center gap-0.5 rounded-lg bg-white/10 px-3 py-1.5">
                <Share className="h-4 w-4 text-sky-400" />
                <span className="text-[9px] text-slate-400">{t("install.share")}</span>
              </div>
              <span className="text-slate-500 text-xs">→</span>
              <div className="flex flex-col items-center gap-0.5 rounded-lg bg-white/10 px-3 py-1.5">
                <Plus className="h-4 w-4 text-sky-400" />
                <span className="text-[9px] text-slate-400">{t("install.add")}</span>
              </div>
            </div>
            {/* 설치했음 — '홈 화면에 추가' 우측에 적당한 간격, 블루 버튼 */}
            <button
              onClick={handleInstalledCheck}
              className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-[11px] font-bold text-white hover:bg-blue-500 transition-colors"
            >
              {t("install.installed") || "설치했음"}
            </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
